import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {MatLineModule} from '@angular/material/core';
import {CommonModule} from '@angular/common';
import {MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {UserService} from '../api/services/user.service';
import {Subscription} from 'rxjs';
import {ViewService} from '../api/services/view.service';
import {CreateNewViewDialogComponent} from './create-new-view-dialog.component';
import {SavedView} from '../api/interfaces/saved-view';
import {MapService} from '../api/services/map.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-views-list',
  styleUrls: ['./views-list.bottomsheet.component.scss'],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-object-scene me-2"></span>{{ 'SCENES_V1.LIST.HEADER' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="dismiss()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <mat-nav-list class="list-wrapper">
      <a mat-list-item *ngFor="let view of views"
         (click)="selectView(view); $event.preventDefault(); $event.stopPropagation()"
         class="d-flex justify-content-evenly p-0">
        <span class="row w-100 align-items-center m-0">
          <span class="col-2">
              <mat-icon class="material-symbols-rounded"
                        *ngIf="!view.excludeFromReadOnly"
                        [matTooltip]="'SCENES_V1.LIST.TOOLTIP_VISIBLE' | translate"
                        matTooltipPosition="left">visibility</mat-icon>
              <mat-icon class="material-symbols-rounded"
                        [matTooltip]="'SCENES_V1.LIST.TOOLTIP_HIDDEN' | translate"
                        matTooltipPosition="left"
                        *ngIf="view.excludeFromReadOnly">visibility_off</mat-icon>
          </span>
          <span class="col-6 pe-5"
                [matTooltip]="view?.description ? view.description : view.name"
                matTooltipPosition="above">
              <span matLine>{{view.name}}</span>
          </span>
          <span class="col-4  d-flex justify-content-end">
            <button mat-icon-button
                    [matTooltip]="'SCENES_V1.LIST.TOOLTIP_EDIT' | translate"
                    matTooltipPosition="above"
                    (click)="edit(view);$event.preventDefault();  $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">edit</mat-icon>
            </button>

            <button mat-icon-button
                    [matTooltip]="'SCENES_V1.LIST.TOOLTIP_DELETE' | translate"
                    matTooltipPosition="above"
                    (click)="askForConfirmation(view);$event.preventDefault();  $event.stopPropagation()">
                    <mat-icon class="material-symbols-rounded">delete</mat-icon>
            </button>
          </span>
        </span>
      </a>
    </mat-nav-list>
    <mat-divider></mat-divider>
    <div class="py-2" style="padding-left: 10px;">
          <button class="dialog-action-btn dialog-action-btn-primary"
                  [matTooltip]="'SCENES_V1.LIST.TOOLTIP_CREATE' | translate"
                  (click)="createNew(); $event.preventDefault();">
              {{ 'SCENES_V1.LIST.CREATE_SCENE' | translate }}
          </button>
    </div>
  `,

  standalone: true,
  imports: [
    MatListModule,
    CommonModule,
    MatLineModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslatePipe,
  ]
})

export class ViewsListBottomsheetComponent implements OnInit, OnDestroy {
  public views: any[] = [];
  private subSink: Subscription = new Subscription();

  constructor(private matDialog: MatDialog,
              private userService: UserService,
              private viewService: ViewService,
              private mapService: MapService,
              private translate: TranslateService,
              private bottomSheetRef: MatBottomSheetRef<ViewsListBottomsheetComponent>) {
  }

  ngOnInit() {
    this.subSink.add(
      (isInIframe() ? this.viewService.getAllPublic(this.mapService.getCurrentSelectedMapFromStore().id) : this.viewService.getAll(this.mapService.getCurrentSelectedMapFromStore().id))
        .subscribe((views) => {
          this.views = views;
        })
    );
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  selectView(view: SavedView) {
    this.bottomSheetRef.dismiss(view);
  }

  createNew() {
    const ref = this.matDialog.open(CreateNewViewDialogComponent, {width: '35%', autoFocus: 'input'});
    ref.afterClosed().subscribe((view: SavedView) => {
      const newView = {...view, isNew: true};
      if (view) {
        this.bottomSheetRef.dismiss(newView);
      }
    });
  }

  edit(view: SavedView) {
    const ref = this.matDialog.open(CreateNewViewDialogComponent, {data: view, width: '35%', autoFocus: 'input'});
    ref.afterClosed().subscribe((v: SavedView) => {
      if (v) {
        this.views = this.views.map((view) => view.id === v.id ? v : view);
      }
    });
  }

  delete(view: SavedView) {
    view.map = this.mapService.getCurrentSelectedMapFromStore();
    this.viewService.delete(view, () => this.views = this.views.filter((v) => v.id !== view.id));
  }

  dismiss() {
    this.bottomSheetRef.dismiss();
  }

  askForConfirmation(view: SavedView) {
    const ref = this.matDialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('SCENES_V1.LIST.DELETE_CONFIRM_TITLE'),
        icon: 'delete',
        message: this.translate.instant('SCENES_V1.LIST.DELETE_CONFIRM_MESSAGE', {name: view.name}),
        confirmText: this.translate.instant('DASHBOARD_MSG.MENU.DELETE')
      }
    });
    ref.afterClosed().subscribe((res) => {
      if (res) {
        this.delete(view);
      }
    });
  }
}
